export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export const VALID_PRODUCT_TYPES = ['fullApp', 'combined'] as const;

export type ValidProductType = typeof VALID_PRODUCT_TYPES[number];

export const validateProductId = (id: string): boolean => /^\d+$/.test(id);

export const validateProductSlug = (slug: string): boolean =>
  /^[A-Za-z0-9]{1,10}_\d{1,5}$/.test(slug);

/**
 * Normalizes productType from URL format to internal enum format.
 *
 * URL vs Internal Format Case Transformation:
 * - URLs use lowercase for SEO-friendliness: 'fullapp', 'combined'
 * - Internal code uses camelCase matching TypeScript enum: 'fullApp', 'combined'
 *
 * Example URL: /loan-application/fullapp/626/ZP3AZH8B_410/
 * Internal value after normalization: 'fullApp'
 *
 * @param urlType - The productType as it appears in the URL (lowercase)
 * @returns The normalized productType matching internal enum format (camelCase)
 *
 * @example
 * normalizeProductType('fullapp') // returns 'fullApp'
 * normalizeProductType('combined') // returns 'combined'
 */
export const normalizeProductType = (urlType: string): string => {
  const mapping: Record<string, string> = {
    fullapp: 'fullApp',
    combined: 'combined',
  };
  return mapping[urlType.toLowerCase()] || urlType;
};

/**
 * Validates that a productType string is one of the valid product types.
 *
 * @param type - The productType to validate (should already be normalized)
 * @returns True if the productType is valid, false otherwise
 */
export const validateProductType = (type: string): boolean =>
  VALID_PRODUCT_TYPES.includes(type as ValidProductType);

/**
 * Type guard that validates and narrows productType to ValidProductType.
 * Use this when TypeScript type narrowing is needed.
 *
 * @param type - The productType to validate
 * @returns True if valid, with TypeScript type narrowing to ValidProductType
 */
export const isValidProductType = (type: string): type is ValidProductType =>
  VALID_PRODUCT_TYPES.includes(type as ValidProductType);

export const validateLendAPIRoute = (productId: string, productSlug: string): ValidationResult => {
  const errors: string[] = [];

  if (!validateProductId(productId)) {
    errors.push('Invalid productId format');
  }

  if (!validateProductSlug(productSlug)) {
    errors.push('Invalid productSlug format');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
